(() => {
  const elementName = 'ce-recently-viewed';
  if (customElements.get(elementName)) return;

  const storageKey = 'ce-recently-viewed-v1';
  const historyLimit = 12;
  const validHandle = /^[a-z0-9][a-z0-9-]*$/;

  class CeRecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ceReady === 'true') return;
      this.dataset.ceReady = 'true';
      this.abortController = new AbortController();
      this.load();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    readHistory() {
      try {
        const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
        if (!Array.isArray(stored)) return [];

        return [...new Set(stored
          .map((item) => typeof item === 'string' ? item : item?.handle)
          .filter((handle) => typeof handle === 'string' && validHandle.test(handle))
        )];
      } catch (_error) {
        return [];
      }
    }

    writeHistory(handles) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(handles.slice(0, historyLimit)));
      } catch (_error) {
        // The section remains optional when browser storage is unavailable.
      }
    }

    get rootUrl() {
      const root = window.Shopify?.routes?.root || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    async fetchProduct(handle) {
      try {
        const response = await fetch(`${this.rootUrl}products/${encodeURIComponent(handle)}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: this.abortController.signal,
        });

        if (!response.ok) return null;
        return { handle, product: await response.json() };
      } catch (error) {
        if (error.name !== 'AbortError') return null;
        throw error;
      }
    }

    formatMoney(cents) {
      const amount = Number(cents);
      const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      if (!Number.isFinite(amount)) return '';

      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency,
        }).format(amount / 100);
      } catch (_error) {
        return `${currency} ${(amount / 100).toFixed(2)}`;
      }
    }

    imageUrl(source, width = 720) {
      const rawSource = typeof source === 'string' ? source : source?.src;
      if (!rawSource) return '';

      try {
        const url = new URL(rawSource.startsWith('//') ? `https:${rawSource}` : rawSource, window.location.origin);
        if (!['http:', 'https:'].includes(url.protocol)) return '';
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch (_error) {
        return '';
      }
    }

    createCard({ handle, product }) {
      const template = this.querySelector('template');
      if (!template || !product?.title) return null;

      const card = template.content.firstElementChild?.cloneNode(true);
      if (!card) return null;

      const link = card.querySelector('[data-product-link]');
      const image = card.querySelector('[data-product-image]');
      const title = card.querySelector('[data-product-title]');
      const price = card.querySelector('[data-product-price]');
      const source = product.featured_image || product.images?.[0];
      const sourceUrl = this.imageUrl(source);

      link.href = `${this.rootUrl}products/${encodeURIComponent(handle)}`;
      title.textContent = product.title;

      if (sourceUrl) {
        image.src = sourceUrl;
        image.alt = product.title;
      } else {
        image.closest('.ce-recently-viewed__media')?.remove();
      }

      const minimumPrice = product.price_min ?? product.price;
      const maximumPrice = product.price_max ?? product.price;
      const formattedPrice = this.formatMoney(minimumPrice);
      const isRange = Number(maximumPrice) > Number(minimumPrice);
      const fromPrice = this.dataset.fromPrice || '__CE_PRICE__';
      price.textContent = isRange ? fromPrice.replace('__CE_PRICE__', formattedPrice) : formattedPrice;

      return card;
    }

    async load() {
      const currentHandle = this.dataset.currentHandle;
      if (!currentHandle || !validHandle.test(currentHandle)) return;

      const history = this.readHistory();
      const previousHandles = history.filter((handle) => handle !== currentHandle);
      this.writeHistory([currentHandle, ...previousHandles]);

      const productsToShow = Math.min(Math.max(Number(this.dataset.productsToShow) || 4, 2), 6);
      const handlesToLoad = previousHandles.slice(0, productsToShow);
      if (!handlesToLoad.length) return;

      const results = await Promise.allSettled(handlesToLoad.map((handle) => this.fetchProduct(handle)));
      const grid = this.querySelector('[data-recently-viewed-grid]');
      if (!grid || !this.isConnected) return;

      results.forEach((result) => {
        if (result.status !== 'fulfilled' || !result.value) return;
        const card = this.createCard(result.value);
        if (card) grid.append(card);
      });

      if (!grid.children.length) return;
      this.removeAttribute('hidden');
      requestAnimationFrame(() => this.classList.add('is-ready'));
    }
  }

  customElements.define(elementName, CeRecentlyViewed);
})();
